import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Table, InputNumber, Input, Button, message, Statistic,
  Row, Col, Space, Tag, Progress, Spin, Select, Alert, Descriptions, Modal
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, CheckCircleOutlined,
  ClockCircleOutlined, TrophyOutlined, BarChartOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';

const { TextArea } = Input;
const { Option } = Select;

const RectificacionCalificar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendiente } = location.state || {};

  // pendiente contiene: IdSolicitudCurso, IdSolicitud, IdAsignacionDocente,
  // IdAlumno, NombreAlumno, Matricula, NombreCurso, NombreGrado,
  // NombreSeccion, NombreJornada, Motivo, CicloEscolar

  const [unidades, setUnidades]           = useState([]);
  const [unidadSel, setUnidadSel]         = useState(null);
  const [actividades, setActividades]     = useState([]);
  const [actividadSel, setActividadSel]   = useState(null);
  const [alumno, setAlumno]               = useState(null);   // datos del alumno en la actividad
  const [calificacion, setCalificacion]   = useState({});     // { Punteo, Observaciones, IdCalificacion }
  const [loadingUnidades, setLoadingUnidades]   = useState(false);
  const [loadingActividades, setLoadingActividades] = useState(false);
  const [loadingAlumno, setLoadingAlumno] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const [guardando, setGuardando]         = useState(false);
  const [completando, setCompletando]     = useState(false);

  useEffect(() => {
    if (!pendiente) {
      message.error('No se encontró información del pendiente.');
      navigate('/dashboard/docente/rectificacion-notas');
      return;
    }
    cargarUnidades();
  }, []);

  useEffect(() => {
    if (unidadSel) cargarActividades(unidadSel);
  }, [unidadSel]);

  useEffect(() => {
    if (actividadSel) cargarDatosAlumno(actividadSel);
  }, [actividadSel]);

  const cargarUnidades = async () => {
    setLoadingUnidades(true);
    try {
      const res = await apiClient.get(`/unidades/asignacion/${pendiente.IdAsignacionDocente}`);
      const data = res.data.data || res.data || [];
      setUnidades(data);
      // Seleccionar la primera unidad abierta automáticamente
      const primera = data.find(u => !u.Cerrada && !u.EstaCerrada) || data[0];
      if (primera) setUnidadSel(primera.IdUnidad);
    } catch {
      message.error('Error al cargar unidades.');
    } finally {
      setLoadingUnidades(false);
    }
  };

  const cargarActividades = async (idUnidad) => {
    setLoadingActividades(true);
    setActividadSel(null);
    setAlumno(null);
    setCalificacion({});
    try {
      const res = await apiClient.get(`/actividades/unidad/${idUnidad}`);
      const data = (res.data.data || []).filter(a => a.Estado === true || a.Estado === 1);
      setActividades(data);
    } catch {
      message.error('Error al cargar actividades.');
    } finally {
      setLoadingActividades(false);
    }
  };

  const cargarDatosAlumno = async (idActividad) => {
    setLoadingAlumno(true);
    setCalificacion({});
    try {
      const res = await apiClient.get(`/actividades/${idActividad}/alumnos`);
      const todos = res.data.data || [];
      const encontrado = todos.find(a => a.IdAlumno === pendiente.IdAlumno);
      if (!encontrado) {
        message.warning('El alumno no aparece en esta actividad.');
        setAlumno(null);
        return;
      }
      setAlumno(encontrado);
      if (encontrado.IdCalificacion) {
        setCalificacion({
          IdCalificacion: encontrado.IdCalificacion,
          Punteo: encontrado.Punteo,
          Observaciones: encontrado.Observaciones,
        });
      }
    } catch {
      message.error('Error al cargar datos del alumno.');
    } finally {
      setLoadingAlumno(false);
    }
  };

  const handleGuardar = async () => {
    if (calificacion.Punteo === undefined || calificacion.Punteo === null) {
      message.warning('Ingresa un punteo antes de guardar.');
      return;
    }
    setGuardando(true);
    try {
      const res = await apiClient.post(
        `/calificaciones/actividad/${actividadSel}/batch-solicitud`,
        {
          calificaciones: [{
            IdAlumno: pendiente.IdAlumno,
            Punteo: parseFloat(calificacion.Punteo),
            Observaciones: calificacion.Observaciones || null,
          }],
        }
      );
      if (res.data.success) {
        message.success('Calificación guardada correctamente.');
        await cargarDatosAlumno(actividadSel);
      }
    } catch (e) {
      if (e.response?.data?.unidadCerrada) {
        message.error('La unidad está cerrada. Solicita reapertura al administrador.');
      } else {
        message.error(e.response?.data?.error || 'Error al guardar.');
      }
    } finally {
      setGuardando(false);
    }
  };

  const marcarCompletado = () => {
    modal.confirm({
      title: 'Marcar curso como completado',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Confirmas que ya ingresaste todas las notas necesarias de <strong>{pendiente.NombreAlumno}</strong> en <strong>{pendiente.NombreCurso}</strong>?</p>
          <p style={{ color: '#888', fontSize: 12 }}>Esta acción no se puede deshacer.</p>
        </div>
      ),
      okText: 'Sí, completado',
      cancelText: 'Cancelar',
      onOk: async () => {
        setCompletando(true);
        try {
          const res = await apiClient.put(
            `/solicitudes-calificacion/cursos/${pendiente.IdSolicitudCurso}/completar`
          );
          if (res.data.solicitudCompletada) {
            message.success('¡Todos los cursos de la solicitud han sido completados!');
          } else {
            message.success('Curso marcado como completado.');
          }
          navigate('/dashboard/docente/rectificacion-notas');
        } catch (e) {
          message.error(e.response?.data?.error || 'Error al completar.');
        } finally {
          setCompletando(false);
        }
      },
    });
  };

  const actividadActual = actividades.find(a => a.IdActividad === actividadSel);
  const punteoMax = parseFloat(actividadActual?.PunteoMaximo || 0);
  const punteoActual = parseFloat(calificacion.Punteo || 0);

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Card
        title={
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Button type="link" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0 }}
              onClick={() => navigate('/dashboard/docente/rectificacion-notas')}>
              Volver a Rectificación de Notas
            </Button>
            <div style={{ fontSize: 20, fontWeight: 'bold' }}>
              Rectificación — {pendiente?.NombreCurso}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              {pendiente?.NombreGrado} {pendiente?.NombreSeccion} — {pendiente?.NombreJornada}
            </div>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            size="large"
            loading={completando}
            onClick={marcarCompletado}
          >
            Marcar Completado
          </Button>
        }
      >
        {/* Info del alumno y motivo */}
        <Descriptions size="small" bordered column={3} style={{ marginBottom: 20 }}>
          <Descriptions.Item label="Alumno" span={2}>
            <strong>{pendiente?.NombreAlumno}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Matrícula">{pendiente?.Matricula}</Descriptions.Item>
          <Descriptions.Item label="Motivo" span={3}>
            <Tag color="orange" style={{ whiteSpace: 'normal' }}>{pendiente?.Motivo}</Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Selectores unidad + actividad */}
        <Space wrap style={{ marginBottom: 20 }}>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Unidad</div>
            <Select
              style={{ width: 220 }}
              placeholder="Selecciona una unidad"
              value={unidadSel}
              onChange={setUnidadSel}
              loading={loadingUnidades}
            >
              {unidades.map(u => (
                <Option key={u.IdUnidad} value={u.IdUnidad}>
                  {u.NombreUnidad}
                  {(u.Cerrada || u.EstaCerrada) && <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>Cerrada</Tag>}
                </Option>
              ))}
            </Select>
          </div>

          {unidadSel && (
            <div>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Actividad</div>
              <Select
                style={{ width: 260 }}
                placeholder="Selecciona una actividad"
                value={actividadSel}
                onChange={setActividadSel}
                loading={loadingActividades}
              >
                {actividades.map(a => (
                  <Option key={a.IdActividad} value={a.IdActividad}>
                    <Space>
                      {a.NombreActividad}
                      <Tag color={a.TipoActividad === 'zona' ? 'blue' : 'green'} style={{ fontSize: 10 }}>
                        {a.TipoActividad === 'zona' ? 'Zona' : 'Final'}
                      </Tag>
                      <span style={{ color: '#888', fontSize: 11 }}>{a.PunteoMaximo} pts</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </Space>

        {actividades.length === 0 && unidadSel && !loadingActividades && (
          <Alert message="No hay actividades activas en esta unidad." type="info" showIcon style={{ marginBottom: 16 }} />
        )}

        {/* Tabla de calificación — solo el alumno rectificado */}
        {actividadSel && (
          <Spin spinning={loadingAlumno}>
            {alumno ? (
              <>
                {/* Mini estadística */}
                {calificacion.Punteo !== undefined && calificacion.Punteo !== null && (
                  <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f2f5' }}>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Statistic title="Punteo ingresado" value={punteoActual} suffix={`/ ${punteoMax}`}
                          prefix={<TrophyOutlined />} />
                        <Progress
                          percent={punteoMax > 0 ? parseFloat(((punteoActual / punteoMax) * 100).toFixed(1)) : 0}
                          status={punteoActual >= punteoMax * 0.6 ? 'success' : 'exception'}
                          style={{ marginTop: 8 }}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic title="Estado"
                          value={calificacion.IdCalificacion ? 'Guardado' : 'Sin guardar'}
                          valueStyle={{ color: calificacion.IdCalificacion ? '#3f8600' : '#cf1322' }}
                          prefix={calificacion.IdCalificacion ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic title="Tipo actividad"
                          value={actividadActual?.TipoActividad === 'zona' ? 'Zona' : 'Final'} />
                      </Col>
                      <Col span={6}>
                        <Statistic title="Punteo máximo" value={punteoMax} suffix="pts"
                          prefix={<BarChartOutlined />} />
                      </Col>
                    </Row>
                  </Card>
                )}

                <Table
                  bordered
                  pagination={false}
                  scroll={{ x: 800 }}
                  dataSource={[alumno]}
                  rowKey="IdAlumno"
                  rowClassName={() => calificacion.Punteo === undefined || calificacion.Punteo === null ? 'row-pendiente' : ''}
                  columns={[
                    { title: 'Carnet', dataIndex: 'IdAlumno', key: 'id', width: 110, fixed: 'left' },
                    { title: 'Apellidos', dataIndex: 'Apellidos', key: 'ap', width: 180, fixed: 'left' },
                    { title: 'Nombres', dataIndex: 'Nombres', key: 'nom', width: 180 },
                    {
                      title: `Punteo (0 - ${punteoMax} pts)`,
                      key: 'punteo', width: 180,
                      render: () => (
                        <InputNumber
                          min={0}
                          max={punteoMax}
                          step={0.25}
                          precision={2}
                          value={calificacion.Punteo}
                          onChange={(v) => setCalificacion(p => ({ ...p, Punteo: v }))}
                          style={{ width: '100%' }}
                          placeholder="0.00"
                        />
                      ),
                    },
                    {
                      title: 'Observaciones', key: 'obs', width: 280,
                      render: () => (
                        <TextArea
                          rows={1}
                          value={calificacion.Observaciones || ''}
                          onChange={(e) => setCalificacion(p => ({ ...p, Observaciones: e.target.value }))}
                          placeholder="Observaciones opcionales"
                          maxLength={200}
                        />
                      ),
                    },
                    {
                      title: 'Estado', key: 'est', width: 120, align: 'center',
                      render: () => calificacion.IdCalificacion ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">Guardado</Tag>
                      ) : (
                        <Tag icon={<ClockCircleOutlined />} color="warning">Pendiente</Tag>
                      ),
                    },
                  ]}
                />

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={guardando}
                    onClick={handleGuardar}
                  >
                    Guardar Calificación
                  </Button>
                </div>
              </>
            ) : (
              !loadingAlumno && (
                <Alert
                  message="Alumno no encontrado en esta actividad"
                  description="Es posible que el alumno no esté inscrito en esta actividad específica. Verifica con el administrador."
                  type="warning"
                  showIcon
                />
              )
            )}
          </Spin>
        )}
      </Card>

      <style>{`
        .row-pendiente { background-color: #fffbe6; }
        .row-pendiente:hover { background-color: #fff7cc !important; }
      `}</style>
    </div>
  );
};

export default RectificacionCalificar;
